import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ThemeProvider,
  Typography,
} from '@mui/material';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  reload,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { auth } from '../firebaseConfig';
import darssyTheme from '../theme/darssyTheme';
import {
  invitationApi,
  invitationError,
  parseInvitationToken,
} from '../services/classInvitationService';
import { setStoredTutorType } from '../utils/roleTerminology';

export default function StudentJoinClass() {
  const { token: routeToken } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const token = parseInvitationToken(routeToken);
  const [invitation, setInvitation] = useState(null);
  const [input, setInput] = useState('');
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [loading, setLoading] = useState(!!token);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState('register');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(
    () =>
      onAuthStateChanged(auth, (current) => {
        setUser(current);
        setAuthReady(true);
        setStatus('');
        if (current?.displayName) {
          const [first, ...rest] = current.displayName.split(' ');
          setFirstName(first);
          setLastName(rest.join(' '));
        }
      }),
    []
  );

  useEffect(() => {
    let current = true;
    setInvitation(null);
    setStatus('');
    setError('');
    setNotice('');
    if (!token) {
      setLoading(false);
      if (routeToken) setError(t('This invitation is invalid. Ask your teacher for a new link.'));
      return undefined;
    }
    setLoading(true);
    invitationApi('preview', { token }, true)
      .then((result) => {
        if (current) setInvitation(result);
      })
      .catch((e) => {
        if (current) setError(invitationError(e, t));
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [token, routeToken, t]);

  const join = async () => {
    const result = await invitationApi('join', { token, firstName, lastName, phone });
    setStatus(result.status);
    setNeedsVerification(false);
    if (result.status === 'enrolled') {
      setStoredTutorType(result.tutorType);
      navigate(`/student/class/${result.groupId}`, { replace: true });
    }
  };
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    setNotice('');
    let accountCreatedNow = false;
    try {
      if (!auth.currentUser) {
        if (mode === 'register') {
          if (password !== confirmPassword) {
            setError(t('Passwords do not match.'));
            return;
          }
          const credential = await createUserWithEmailAndPassword(
            auth,
            email.trim().toLowerCase(),
            password
          );
          accountCreatedNow = true;
          await updateProfile(credential.user, {
            displayName: `${firstName.trim()} ${lastName.trim()}`,
          });
        } else {
          await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
        }
        setPassword('');
        setConfirmPassword('');
      }
      if (needsVerification) {
        await reload(auth.currentUser);
        await auth.currentUser.getIdToken(true);
      }
      await join();
    } catch (e) {
      // An account created a moment ago for a join that then failed is
      // left with no student record and no request — its email taken for
      // good ("already exists" on sign-up, "does not exist" on sign-in).
      // Undo it, unless the join is only waiting on email verification,
      // which needs that very account.
      if (accountCreatedNow && e.code !== 'verify-email' && auth.currentUser) {
        await auth.currentUser.delete().catch(() => {});
      }
      setError(invitationError(e, t));
      setNeedsVerification(e.code === 'verify-email');
    } finally {
      setBusy(false);
    }
  };
  const resetPassword = async () => {
    if (!email.trim()) {
      setError(t('Please enter a valid email address.'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      auth.languageCode = i18n.language;
      await sendPasswordResetEmail(auth, email.trim().toLowerCase());
      setNotice(t('If an account exists for this email, a password reset link has been sent.'));
    } catch (e) {
      setError(invitationError(e, t));
    } finally {
      setBusy(false);
    }
  };
  const verifyEmail = async () => {
    setBusy(true);
    setError('');
    try {
      auth.languageCode = i18n.language;
      await sendEmailVerification(auth.currentUser, {
        url: `${window.location.origin}/student/join/${token}`,
      });
      setNotice(t('Check your email, complete verification, then return here and continue.'));
    } catch (e) {
      setError(invitationError(e, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ThemeProvider theme={darssyTheme}>
      <Box
        dir={i18n.dir()}
        sx={{
          minHeight: '100vh',
          bgcolor: '#f7faff',
          p: { xs: 2, sm: 4 },
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 480,
            p: { xs: 2.5, sm: 4 },
            border: '1px solid #e4ecfa',
            borderRadius: 3,
          }}
        >
          <Stack spacing={2.2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography fontWeight={800} letterSpacing={2} color="primary">
                DARSSY
              </Typography>
              <TextField
                select
                size="small"
                value={i18n.resolvedLanguage || 'en'}
                onChange={(e) => i18n.changeLanguage(e.target.value)}
                SelectProps={{ inputProps: { 'aria-label': t('Language') } }}
              >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="fr">Français</MenuItem>
                <MenuItem value="ar">العربية</MenuItem>
              </TextField>
            </Stack>
            <Box
              sx={{
                width: 50,
                height: 50,
                display: 'grid',
                placeItems: 'center',
                bgcolor: '#eaf2ff',
                color: 'primary.main',
                borderRadius: 2,
              }}
            >
              <PersonAddAltRoundedIcon />
            </Box>
            <Box>
              <Typography component="h1" variant="h5" fontWeight={700}>
                {t('Join a class')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t(
                  'Use your teacher’s invitation to create an account or join with your existing account.'
                )}
              </Typography>
            </Box>
            {error && <Alert severity="error">{error}</Alert>}
            {notice && <Alert severity="info">{notice}</Alert>}
            {(loading || !authReady) && (
              <Box role="status" aria-label={t('Loading')} sx={{ textAlign: 'center' }}>
                <CircularProgress size={26} />
              </Box>
            )}
            {!invitation && !loading && (
              <Box
                component="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  const code = parseInvitationToken(input);
                  if (!code) {
                    setError(t('Paste a valid invitation link or code.'));
                    return;
                  }
                  if (code === token) {
                    window.location.reload();
                    return;
                  }
                  navigate(`/student/join/${code}`);
                }}
              >
                <TextField
                  label={t('Invitation link or code')}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  fullWidth
                  required
                  size="small"
                  inputProps={{ dir: 'ltr' }}
                />
                <Button type="submit" fullWidth variant="contained" sx={{ mt: 2 }}>
                  {t('Continue')}
                </Button>
              </Box>
            )}
            {invitation && authReady && (
              <>
                <Box sx={{ p: 2, bgcolor: '#f2f6ff', borderRadius: 2 }}>
                  <Typography fontWeight={700} dir="auto">
                    {invitation.groupName}
                  </Typography>
                  {invitation.teacherName && (
                    <Typography variant="body2" color="text.secondary" dir="auto">
                      {invitation.teacherName}
                    </Typography>
                  )}
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    {t(
                      invitation.approvalRequired
                        ? 'Your teacher will approve your request before you can access the class.'
                        : 'You will be enrolled automatically after joining.'
                    )}
                  </Typography>
                </Box>
                {status === 'pending' && (
                  <Alert icon={<CheckCircleOutlineRoundedIcon />} severity="success">
                    {t(
                      'Request sent. Your teacher must approve it before you can access the class. Once approved, sign in with this same email and password — no need to create a new account.'
                    )}
                  </Alert>
                )}
                <Box component="form" onSubmit={submit}>
                  <Stack spacing={2}>
                    {user ? (
                      <>
                        <Typography variant="body2">
                          {t('Signed in as')} <b dir="ltr">{user.email}</b>
                        </Typography>
                        {!status && (
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                            <TextField
                              label={t('First name')}
                              value={firstName}
                              onChange={(e) => setFirstName(e.target.value)}
                              size="small"
                              fullWidth
                              autoComplete="given-name"
                              inputProps={{ maxLength: 120 }}
                            />
                            <TextField
                              label={t('Last name')}
                              value={lastName}
                              onChange={(e) => setLastName(e.target.value)}
                              size="small"
                              fullWidth
                              autoComplete="family-name"
                              inputProps={{ maxLength: 120 }}
                            />
                          </Stack>
                        )}
                      </>
                    ) : (
                      <>
                        <Stack direction="row" spacing={1}>
                          <Button
                            fullWidth
                            variant={mode === 'register' ? 'contained' : 'outlined'}
                            disabled={busy}
                            onClick={() => setMode('register')}
                          >
                            {t('Create account')}
                          </Button>
                          <Button
                            fullWidth
                            variant={mode === 'login' ? 'contained' : 'outlined'}
                            disabled={busy}
                            onClick={() => setMode('login')}
                          >
                            {t('Sign in')}
                          </Button>
                        </Stack>
                        {mode === 'register' && (
                          <>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                              <TextField
                                label={t('First name')}
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                size="small"
                                fullWidth
                                required
                                autoComplete="given-name"
                                inputProps={{ maxLength: 120 }}
                              />
                              <TextField
                                label={t('Last name')}
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                size="small"
                                fullWidth
                                required
                                autoComplete="family-name"
                                inputProps={{ maxLength: 120 }}
                              />
                            </Stack>
                            <TextField
                              label={t('Phone (optional)')}
                              value={phone}
                              onChange={(e) => setPhone(e.target.value)}
                              size="small"
                              type="tel"
                              autoComplete="tel"
                              inputProps={{ maxLength: 40 }}
                            />
                          </>
                        )}
                        <TextField
                          label={t('Email')}
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          size="small"
                          required
                          autoComplete="email"
                          inputProps={{ dir: 'ltr' }}
                        />
                        <TextField
                          label={t('Password')}
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          size="small"
                          required
                          autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                          inputProps={{ minLength: 6 }}
                        />
                        {mode === 'register' && (
                          <TextField
                            label={t('Confirm password')}
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            size="small"
                            required
                            autoComplete="new-password"
                            inputProps={{ minLength: 6 }}
                          />
                        )}
                        {mode === 'login' && (
                          <Button size="small" disabled={busy} onClick={resetPassword}>
                            {t('Forgot password?')}
                          </Button>
                        )}
                      </>
                    )}
                    {needsVerification && (
                      <Button disabled={busy} onClick={verifyEmail}>
                        {t('Send verification email')}
                      </Button>
                    )}
                    <Button type="submit" variant="contained" disabled={busy} fullWidth>
                      {busy ? (
                        <CircularProgress size={22} color="inherit" />
                      ) : (
                        t(
                          status === 'pending'
                            ? 'Check approval status'
                            : needsVerification
                              ? 'I verified my email — continue'
                              : invitation.approvalRequired
                                ? 'Request to join'
                                : 'Join class'
                        )
                      )}
                    </Button>
                    {user && (
                      <Button
                        disabled={busy}
                        size="small"
                        onClick={async () => {
                          try {
                            await signOut(auth);
                            setStatus('');
                            setNeedsVerification(false);
                            setFirstName('');
                            setLastName('');
                            setError('');
                          } catch (e) {
                            setError(invitationError(e, t));
                          }
                        }}
                      >
                        {t('Use another account')}
                      </Button>
                    )}
                  </Stack>
                </Box>
              </>
            )}
            <Divider />
            <Button onClick={() => navigate('/student/login')}>{t('Back to student login')}</Button>
          </Stack>
        </Paper>
      </Box>
    </ThemeProvider>
  );
}
